import React from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';
import StructuredListInput from './StructuredListInput';
import { BILINGUAL_REQUIRED } from '../../utils/formValidation';

/**
 * EnhancedViralForm - Viral pack form with Input Quality Layer enhancements
 * Focus: Viral hooks, creative patterns, engagement tactics (FLEXIBLE LANGUAGE)
 */
const EnhancedViralForm = ({ 
  content = {}, 
  onChange, 
  errors = {}, 
  touched = {},
  onFieldBlur,
  packType = 'viral'
}) => {
  const handleChange = (field, value) => {
    onChange({ ...content, [field]: value });
  };

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: EMZI_SPACING.md,
    }}>
      {/* Flexible Language Notice */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#FEF3C7',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: '#92400E',
        borderLeft: `3px solid ${EMZI_COLORS.warning}`,
      }}>
        ✨ <strong>Creative Freedom:</strong> Viral packs can be single-language (core pattern) with optional bilingual hooks. Focus on what resonates!
      </div>

      {/* Viral Hook/Pattern */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Viral Hook/Pattern
          <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 The core viral concept. Example: "Before/After transformation story" or "Relatable Muslim mom struggles with health"
        </div>

        <textarea
          value={content.viral_hook || ''}
          onChange={(e) => handleChange('viral_hook', e.target.value)}
          onBlur={() => onFieldBlur && onFieldBlur('viral_hook')}
          placeholder="e.g., Before/After transformation story showing Muslim mom's health journey"
          rows={3}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${touched.viral_hook && errors.content?.viral_hook ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
        {touched.viral_hook && errors.content?.viral_hook && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: EMZI_COLORS.error,
          }}>
            {errors.content.viral_hook}
          </div>
        )}
      </div>

      {/* Emotional Triggers */}
      <StructuredListInput
        label="Emotional Triggers"
        items={content.emotional_triggers || []}
        onChange={(items) => handleChange('emotional_triggers', items)}
        minItems={1}
        maxItems={8}
        placeholder="Add an emotional trigger..."
        helper="What emotions to evoke. Example: 'Hope for better health' or 'Fear of missing out' or 'Pride in cultural identity'"
        required={true}
        hasError={touched.emotional_triggers && errors.content?.emotional_triggers}
        errorMessage={errors.content?.emotional_triggers}
      />

      {/* Key Phrases/Catchphrases */}
      <StructuredListInput
        label="Key Phrases/Catchphrases"
        items={content.key_phrases || []}
        onChange={(items) => handleChange('key_phrases', items)}
        minItems={0}
        maxItems={10}
        placeholder="Add a catchy phrase..."
        helper="Memorable phrases agents can use. Example: 'Halal inside, healthy outside' or 'Untuk keluarga tersayang'"
        required={false}
        hasError={false}
      />

      {/* Target Platforms */}
      <StructuredListInput
        label="Target Platforms"
        items={content.target_platforms || []}
        onChange={(items) => handleChange('target_platforms', items)}
        minItems={0}
        maxItems={8}
        placeholder="Add a platform..."
        helper="Where this performs best. Example: 'Instagram Reels' or 'WhatsApp Status' or 'TikTok'"
        required={false}
        hasError={false}
      />

      {/* Content Format */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Content Format
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 How to present this. Example: "15-second video with before/after split screen + upbeat music"
        </div>

        <textarea
          value={content.content_format || ''}
          onChange={(e) => handleChange('content_format', e.target.value)}
          placeholder="e.g., 15-second video with before/after split screen + upbeat music"
          rows={2}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Call to Action (CTA) */}
      <StructuredListInput
        label="Call to Action (CTA)"
        items={content.cta_options || []}
        onChange={(items) => handleChange('cta_options', items)}
        minItems={0}
        maxItems={6}
        placeholder="Add a CTA..."
        helper="Actions you want viewers to take. Example: 'DM us for free consultation' or 'Tag a friend who needs this'"
        required={false}
        hasError={false}
      />

      {/* Success Indicators */}
      <StructuredListInput
        label="Success Indicators"
        items={content.success_indicators || []}
        onChange={(items) => handleChange('success_indicators', items)}
        minItems={0}
        maxItems={6}
        placeholder="Add a success indicator..."
        helper="How to measure if it's working. Example: 'High comment engagement' or 'Shares > 100' or 'Profile visits spike'"
        required={false}
        hasError={false}
      />

      {/* Cautions/Avoid */}
      <StructuredListInput
        label="What to Avoid"
        items={content.cautions || []}
        onChange={(items) => handleChange('cautions', items)}
        minItems={0}
        maxItems={8}
        placeholder="Add a caution..."
        helper="Pitfalls to avoid. Example: 'Don't be too salesy' or 'Avoid controversial topics' or 'Keep it under 20 seconds'"
        required={false}
        hasError={false}
      />

      {/* Completion hint */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#e6f7ed',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: EMZI_COLORS.success,
        borderLeft: `3px solid ${EMZI_COLORS.success}`,
      }}>
        ✅ <strong>Pro Tip:</strong> Viral packs capture cultural moments and emotions. Test early, iterate fast, and focus on authenticity!
      </div>
    </div>
  );
};

export default EnhancedViralForm;
